import React, { useState, useCallback, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";

import { getInitialLocation } from "./utils";
import BulkActionsButton from "./BulkActionsButton";
import LocationRow from "./LocationRow";
import useQueryParams from "src/hooks/useQueryParams";
import { useProject } from "src/contexts/project";

const styles = {
  centerFlex: {
    display: "flex",
    justifyContent: "center",
  },
  multiSelectGrid: {
    display: "flex",
    justifyContent: "space-between",
    position: "sticky",
    top: -20,
    backgroundColor: "white",
    zIndex: 9,
    py: 1,
  },
  spacer: {
    width: 42,
  },
};

export default function RackLocations({
  multiSelect,
  locations,
  openPalletBreakdown,
  displayShipmentId,
  displayAddAnotherLocation,
  displayDeleteLocation,
  displayDuplicate,
  hideLocation,
  projectId,
  disableQuantity,
}) {
  const currentProject = useProject();
  const valuesRef = useRef();
  const [openRemoveLocation, setOpenRemoveLocation] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const { values, setFieldValue } = useFormikContext();
  const params = useQueryParams();
  const shipmentId = params.get("shipmentId");
  const isAddShipmentItem = params.get("addShipmentItem") === "true";
  const receiving = params.get("receiving") === "true";
  const isNonImportedReceiving = receiving && currentProject?.importedAssets === false;

  valuesRef.current = values;
  const [showAllLocations, setShowAllLocations] = useState(!shipmentId);

  const filteredLocationsByShipment = showAllLocations
    ? locations
    : locations.filter(location => location.shipment_id === shipmentId);

  const displayAllLocations =
    locations.length > filteredLocationsByShipment.length && !showAllLocations;

  const handleAddLocation = useCallback(() => {
    const newLocations = [...values.locations, getInitialLocation(undefined, shipmentId)];
    setFieldValue("locations", newLocations);
  }, [values.locations, shipmentId, setFieldValue]);

  const handleCheckboxChange = useCallback(index => {
    setSelectedLocations(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      }
      return [...prev, index];
    });
  }, []);

  const handleDeleteLocations = useCallback(
    indexes => {
      const locations = valuesRef.current.locations;
      const subQty = indexes.reduce((acc, index) => {
        return acc + (locations[index].sub_qty || 0);
      }, 0);
      setFieldValue("deletedQuantity", valuesRef.current.deletedQuantity + subQty);
      const newLocations = locations.filter((_, index) => !indexes.includes(index));
      setFieldValue("locations", newLocations);
    },
    [setFieldValue]
  );

  const handleUpdateLocations = useCallback(
    (selectedLocations, newLocation) => {
      const newLocations = values.locations.map((location, index) => {
        if (selectedLocations.includes(index)) {
          return {
            ...location,
            ...newLocation,
          };
        }
        return location;
      });
      setFieldValue("locations", newLocations);
    },
    [values.locations, setFieldValue]
  );

  const clearSelectedLocations = useCallback(() => {
    setSelectedLocations([]);
  }, []);

  const handleSelectAll = useCallback(
    event => {
      if (event.target.checked) {
        const availableLocations = showAllLocations
          ? locations.map((_, index) => index)
          : locations
              .map((location, index) => (location.shipment_id === shipmentId ? index : null))
              .filter(index => index !== null);
        setSelectedLocations(availableLocations);
      } else {
        setSelectedLocations([]);
      }
    },
    [locations, showAllLocations, shipmentId]
  );

  const memoizedBulkActions = useMemo(
    () =>
      multiSelect && (
        <BulkActionsButton
          selectedLocations={selectedLocations}
          onDeleteLocations={handleDeleteLocations}
          onUpdateLocations={handleUpdateLocations}
          clearSelectedLocations={clearSelectedLocations}
          openPalletBreakdown={openPalletBreakdown}
          projectId={projectId}
        />
      ),

    [
      multiSelect,
      selectedLocations,
      handleDeleteLocations,
      handleUpdateLocations,
      clearSelectedLocations,
      openPalletBreakdown,
      projectId,
    ]
  );

  return (
    <Grid container spacing={1.5}>
      {displayAllLocations && (
        <Grid sx={styles.centerFlex} size={12}>
          <Button variant="text" color="primary" onClick={() => setShowAllLocations(true)}>
            Display All Storage Locations
          </Button>
        </Grid>
      )}
      {multiSelect && (
        <Grid sx={styles.multiSelectGrid} size={12}>
          <Box>
            <FormControlLabel
              sx={{ margin: 0 }}
              control={
                <Checkbox
                  checked={
                    selectedLocations.length > 0 &&
                    selectedLocations.length ===
                      (showAllLocations ? locations.length : filteredLocationsByShipment.length)
                  }
                  indeterminate={
                    selectedLocations.length > 0 &&
                    selectedLocations.length <
                      (showAllLocations ? locations.length : filteredLocationsByShipment.length)
                  }
                  onChange={handleSelectAll}
                />
              }
              label="Select All Locations"
            />
          </Box>
          {memoizedBulkActions}
          <Box sx={styles.spacer} />
        </Grid>
      )}
      {locations.length > 0 &&
        locations.map((location, index) => {
          if (hideLocation && hideLocation({ location, index })) {
            return null;
          }

          if (!showAllLocations && location.shipment_id !== shipmentId) {
            return null;
          }

          return (
            <LocationRow
              key={`location-row-${index}`}
              autoOpen={isAddShipmentItem && locations.length === 1}
              location={location}
              index={index}
              number={index + 1}
              multiSelect={multiSelect}
              selectedLocations={selectedLocations}
              setFieldValue={setFieldValue}
              onCheckboxChange={handleCheckboxChange}
              openRemoveLocation={openRemoveLocation}
              setOpenRemoveLocation={setOpenRemoveLocation}
              onDeleteLocations={handleDeleteLocations}
              projectId={projectId}
              displayShipmentId={displayShipmentId && !isNonImportedReceiving}
              displayDeleteLocation={displayDeleteLocation && !isNonImportedReceiving}
              displayDuplicate={displayDuplicate}
              disableQuantity={disableQuantity}
            />
          );
        })}
      {displayAddAnotherLocation && (
        <Grid size={12}>
          <Button
            variant="outlined"
            color={receiving ? "inherit" : "primary"}
            type="button"
            startIcon={<AddIcon />}
            onClick={handleAddLocation}
            sx={{
              mb: { xs: 2, sm: 0 },
              ...(receiving && {
                color: "grey.600",
                borderColor: "grey.400",
                "&:hover": {
                  borderColor: "grey.500",
                  backgroundColor: "grey.50",
                },
              }),
            }}
          >
            Add Another Location
          </Button>
        </Grid>
      )}
    </Grid>
  );
}
